// src/components/FileManager/hooks/useFileManager.js

import { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "services/supabaseClient";
import { useProfile } from "context/ProfileContext";

/**
 * useFileManager
 * --------------------------------------------------------------------
 * Centralized, org-scoped data-access and recents management.
 *
 * FINAL CONTRACT (ENFORCED):
 *  • Org identity is resolved once and reduced to orgId (primitive)
 *  • No object references are allowed in hook dependencies
 *  • All queries are strictly org-scoped
 *  • Record shape is caller-defined via optional `select`
 */
export default function useFileManager({
  table,
  limit = 100,
  org = null,
  select = "*",
}) {
  const { selectedOrg } = useProfile();

  /* ------------------------------------------------------------------
   * Resolve orgId ONCE (primitive only)
   * ------------------------------------------------------------------ */
  const orgId = org?.id ?? selectedOrg?.id ?? null;

  /* ------------------------------------------------------------------
   * State
   * ------------------------------------------------------------------ */
  const [records, setRecords] = useState([]);
  const [recents, setRecents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const RECENT_KEY = useMemo(() => `recent_${table}`, [table]);

  /* ------------------------------------------------------------------
   * Load recents (org-scoped, primitive deps only)
   * ------------------------------------------------------------------ */
  const loadRecents = useCallback(() => {
    try {
      if (!orgId) {
        setRecents([]);
        return;
      }

      const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");

      const filtered = stored
        .filter((r) => r.org_id === orgId)
        .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        .slice(0, 5);

      setRecents(filtered);
    } catch {
      setRecents([]);
    }
  }, [RECENT_KEY, orgId]);

  /* ------------------------------------------------------------------
   * Fetch records (primitive deps only)
   * ------------------------------------------------------------------ */
  const fetchRecords = useCallback(async () => {
    if (!table || !orgId) {
      setRecords([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from(table)
        .select(select)
        .eq("org_id", orgId)
        .order("updated_at", { ascending: false })
        .limit(limit);

      if (error) throw error;

      setRecords(data || []);
    } catch (err) {
      console.error("❌ useFileManager fetch error:", err);
      setError(err.message);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [table, orgId, limit, select]);

  /* ------------------------------------------------------------------
   * Add to recents
   * ------------------------------------------------------------------ */
  const addToRecents = useCallback(
    (record) => {
      if (!record?.id || !orgId) return;

      const name =
        record.name ||
        record.deal_name ||
        record.portfolio_name ||
        "(Untitled)";

      const updated_at = record.updated_at || new Date().toISOString();

      const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");

      const updated = [
        { id: record.id, name, updated_at, org_id: orgId },
        ...stored.filter((r) => r.id !== record.id),
      ]
        .filter((r) => r.org_id === orgId)
        .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        .slice(0, 25);

      localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
      setRecents(updated.slice(0, 5));
    },
    [RECENT_KEY, orgId]
  );

  /* ------------------------------------------------------------------
   * React to orgId / table changes
   * ------------------------------------------------------------------ */
  useEffect(() => {
    fetchRecords();
    loadRecents();
  }, [fetchRecords, loadRecents]);

  return {
    records,
    recents,
    loading,
    error,

    fetchRecords,
    addToRecents,
  };
}
